import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './NextJsHosting.css';

const NextJsHosting = () => {
    const scrollToPricing = (e) => {
        e.preventDefault();
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
            const headerOffset = 100;
            const elementPosition = pricingSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>Next.js Hosting - Fast SSR/SSG Deployment with Edge CDN | Epicfinal</title>
                <meta name="title" content="Next.js Hosting - Fast SSR/SSG Deployment with Edge CDN | Epicfinal" />
                <meta name="description" content="Deploy Next.js apps from $5.99/mo. SSR & SSG support, Cloudflare Edge CDN, auto-scaling, Git integration, image optimization & instant deployments. Built for React developers." />
                <meta name="keywords" content="Next.js hosting, Next.js deployment, React hosting, SSR hosting, SSG hosting, Vercel alternative, Next.js cloud hosting, serverless Next.js, Next.js edge functions, Next.js CDN, React SSR hosting, Next.js ISR hosting, Jamstack hosting, Node.js hosting" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="Epicfinal" />
                <link rel="canonical" href="https://epicfinal.com/nextjs-hosting" />

                {/* Language & Locale */}
                <html lang="en" />
                <meta property="og:locale" content="en_US" />
                <link rel="alternate" href="https://epicfinal.com/nextjs-hosting" hrefLang="en" />
                <link rel="alternate" href="https://epicfinal.com/nextjs-hosting" hrefLang="x-default" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://epicfinal.com/nextjs-hosting" />
                <meta property="og:title" content="Next.js Hosting - SSR/SSG with Edge CDN | Epicfinal" />
                <meta property="og:description" content="Deploy Next.js apps from $5.99/mo. SSR & SSG support, Cloudflare Edge CDN, auto-scaling, Git integration & instant deployments." />
                <meta property="og:image" content="https://epicfinal.com/android-chrome-512x512.png" />
                <meta property="og:image:alt" content="Epicfinal Next.js Hosting - Modern React Deployment" />
                <meta property="og:site_name" content="Epicfinal" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content="https://epicfinal.com/nextjs-hosting" />
                <meta name="twitter:title" content="Next.js Hosting - Fast SSR/SSG Deployment | Epicfinal" />
                <meta name="twitter:description" content="Deploy Next.js apps from $5.99/mo. SSR & SSG, Edge CDN, auto-scaling & Git integration." />
                <meta name="twitter:image" content="https://epicfinal.com/android-chrome-512x512.png" />

                {/* Schema.org Structured Data */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Next.js Hosting - Fast SSR/SSG Deployment with Edge CDN",
                        "description": "Deploy Next.js applications with optimized hosting, SSR & SSG support, Cloudflare Edge CDN, automatic scaling, and Git integration.",
                        "url": "https://epicfinal.com/nextjs-hosting",
                        "provider": {
                            "@type": "Organization",
                            "name": "Epicfinal",
                            "url": "https://epicfinal.com"
                        },
                        "breadcrumb": {
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                {
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "Home",
                                    "item": "https://epicfinal.com"
                                },
                                {
                                    "@type": "ListItem",
                                    "position": 2,
                                    "name": "Next.js Hosting",
                                    "item": "https://epicfinal.com/nextjs-hosting"
                                }
                            ]
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "Epicfinal Next.js Hosting",
                        "description": "Modern Next.js hosting with SSR & SSG support, Cloudflare Edge CDN, automatic scaling, Git integration, and instant deployments",
                        "brand": {
                            "@type": "Brand",
                            "name": "Epicfinal"
                        },
                        "category": "Web Hosting",
                        "offers": [
                            {
                                "@type": "Offer",
                                "name": "Next.js Starter",
                                "price": "5.99",
                                "priceCurrency": "USD",
                                "priceValidUntil": "2025-12-31",
                                "availability": "https://schema.org/InStock",
                                "url": "https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/s1-web-starter"
                            },
                            {
                                "@type": "Offer",
                                "name": "Next.js Pro",
                                "price": "8.99",
                                "priceCurrency": "USD",
                                "priceValidUntil": "2025-12-31",
                                "availability": "https://schema.org/InStock",
                                "url": "https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/epicfinal-cloud-web-pro"
                            },
                            {
                                "@type": "Offer",
                                "name": "Next.js Business",
                                "price": "15.99",
                                "priceCurrency": "USD",
                                "priceValidUntil": "2025-12-31",
                                "availability": "https://schema.org/InStock",
                                "url": "https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/epicfinal-business"
                            }
                        ],
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.9",
                            "reviewCount": "120",
                            "bestRating": "5",
                            "worstRating": "1"
                        }
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "What is Next.js hosting?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Next.js hosting is specialized hosting optimized for Next.js React applications. It supports Server-Side Rendering (SSR), Static Site Generation (SSG), Incremental Static Regeneration (ISR), and API routes with edge functions for maximum performance."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Does Epicfinal support Next.js SSR and SSG?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Yes, all our Next.js hosting plans fully support Server-Side Rendering (SSR), Static Site Generation (SSG), and Incremental Static Regeneration (ISR). Our infrastructure is optimized for React and Next.js applications."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "What's included in Next.js hosting?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Our Next.js hosting includes NVMe SSD storage, Cloudflare Edge CDN with 200+ global locations, automatic deployments from Git, SSL certificates, image optimization, environment variables support, preview deployments, and analytics & monitoring."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Is this a Vercel alternative?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Yes, Epicfinal Next.js hosting is a cost-effective alternative to Vercel. We offer similar features like Git integration, automatic deployments, edge CDN, and SSR/SSG support at competitive prices starting from $5.99/month."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Does Next.js hosting include Git integration?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Yes, you can connect your GitHub, GitLab, or Bitbucket repository for automatic deployments. Every push triggers a new build and deployment automatically."
                                }
                            }
                        ]
                    }
                `}</script>
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "Next.js",
                        "applicationCategory": "DeveloperApplication",
                        "operatingSystem": "All",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "USD"
                        },
                        "author": {
                            "@type": "Organization",
                            "name": "Vercel"
                        }
                    }
                `}</script>
            </Helmet>

            <div className="nextjs-hosting-page">
                {/* Hero Section */}
                <section className="nextjs-hero">
                    <div className="container">
                        <div className="nextjs-hero-content">
                            <div className="nextjs-logo-badge">
                                <svg viewBox="0 0 180 180" width="60" height="60">
                                    <mask id="mask0" mask-type="alpha">
                                        <circle cx="90" cy="90" r="90" fill="black"/>
                                    </mask>
                                    <g mask="url(#mask0)">
                                        <circle cx="90" cy="90" r="90" fill="black"/>
                                        <path d="M149.508 157.52L69.142 54H54v71.97h12.114V69.384l73.885 95.461a90.304 90.304 0 009.509-7.325z" fill="white"/>
                                        <path d="M115 54h12v72h-12z" fill="white"/>
                                    </g>
                                </svg>
                            </div>
                            <h1 className="nextjs-hero-title">
                                Next.js Hosting
                            </h1>
                            <p className="nextjs-hero-subtitle">
                                Deploy modern React apps at scale
                            </p>
                            <p className="nextjs-hero-description">
                                Lightning-fast hosting optimized for Next.js with automatic scaling, edge functions, and global CDN. Build and deploy in seconds.
                            </p>
                            <div className="nextjs-hero-buttons">
                                <a href="#pricing" onClick={scrollToPricing} className="btn-nextjs btn-primary">
                                    View Plans & Pricing
                                </a>
                                <Link to="/contact1" className="btn-nextjs btn-secondary">
                                    Contact Sales
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="nextjs-features">
                    <div className="container">
                        <div className="nextjs-section-header">
                            <h2>Built for Next.js</h2>
                            <p>Everything your Next.js app needs to thrive</p>
                        </div>
                        <div className="nextjs-features-grid">
                            <div className="nextjs-feature-card">
                                <div className="nextjs-feature-icon">
                                    <i className="las la-rocket"></i>
                                </div>
                                <h3>Instant Deployment</h3>
                                <p>Deploy from Git in seconds with automatic builds and zero configuration.</p>
                            </div>
                            <div className="nextjs-feature-card">
                                <div className="nextjs-feature-icon">
                                    <i className="las la-globe"></i>
                                </div>
                                <h3>Cloudflare CDN</h3>
                                <p>Static assets served from 200+ edge locations worldwide for ultra-low latency.</p>
                            </div>
                            <div className="nextjs-feature-card">
                                <div className="nextjs-feature-icon">
                                    <i className="las la-bolt"></i>
                                </div>
                                <h3>Edge Functions</h3>
                                <p>Run serverless functions at the edge for maximum performance.</p>
                            </div>
                            <div className="nextjs-feature-card">
                                <div className="nextjs-feature-icon">
                                    <i className="las la-expand-arrows-alt"></i>
                                </div>
                                <h3> Scaling</h3>
                                <p>Scale to handle any traffic spike.</p>
                            </div>
                            <div className="nextjs-feature-card">
                                <div className="nextjs-feature-icon">
                                    <i className="las la-code-branch"></i>
                                </div>
                                <h3>Git Integration</h3>
                                <p>Connect to GitHub, GitLab, or Bitbucket for automatic deployments.</p>
                            </div>
                            <div className="nextjs-feature-card">
                                <div className="nextjs-feature-icon">
                                    <i className="las la-chart-line"></i>
                                </div>
                                <h3>Analytics</h3>
                                <p>Real-time analytics to monitor performance and user behavior.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Performance Section */}
                <section className="nextjs-performance">
                    <div className="container">
                        <div className="nextjs-section-header">
                            <h2>Unmatched Performance</h2>
                            <p>Speed that users love</p>
                        </div>
                        <div className="nextjs-performance-grid">
                            <div className="nextjs-perf-card">
                                <div className="nextjs-perf-icon">
                                    <i className="las la-tachometer-alt"></i>
                                </div>
                                <h3>SSR & SSG</h3>
                                <p>Full support for Server-Side Rendering and Static Site Generation</p>
                            </div>
                            <div className="nextjs-perf-card">
                                <div className="nextjs-perf-icon">
                                    <i className="las la-compress"></i>
                                </div>
                                <h3>Automatic Optimization</h3>
                                <p>Image optimization, code splitting, and lazy loading built-in</p>
                            </div>
                            <div className="nextjs-perf-card">
                                <div className="nextjs-perf-icon">
                                    <i className="las la-network-wired"></i>
                                </div>
                                <h3>Cloudflare Edge Network</h3>
                                <p>Content delivered from the nearest edge location to your users</p>
                            </div>
                            <div className="nextjs-perf-card">
                                <div className="nextjs-perf-icon">
                                    <i className="las la-mobile"></i>
                                </div>
                                <h3>Mobile First</h3>
                                <p>Optimized for mobile devices with progressive enhancement</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pricing Plans Section */}
                <section className="nextjs-pricing" id="pricing">
                    <div className="container">
                        <div className="nextjs-section-header">
                            <h2>Next.js Hosting Plans</h2>
                            <p>Deploy your Next.js apps with the best infrastructure</p>
                        </div>
                        <div className="nextjs-pricing-grid">
                            {/* Starter Plan */}
                            <div className="nextjs-pricing-card">
                                <div className="nextjs-pricing-header">
                                    <h3>Next.js Starter</h3>
                                    <p className="nextjs-pricing-tagline">Perfect for personal projects and small Next.js applications</p>
                                </div>
                                <div className="nextjs-pricing-price">
                                    <span className="nextjs-price-amount">$5.99</span>
                                    <span className="nextjs-price-period">USD / Monthly</span>
                                </div>
                                <ul className="nextjs-pricing-features">
                                    <li><i className="las la-check"></i> <strong>1 Next.js Site</strong></li>
                                    <li><i className="las la-check"></i> 5GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (Fair Use)</li>
                                    <li><i className="las la-check"></i> SSR & SSG Support</li>
                                    <li><i className="las la-check"></i> Automatic Deployments</li>
                                    <li><i className="las la-check"></i> Malware Protection</li>
                                    <li><i className="las la-check"></i> Free SSL Certificates</li>
                                    <li><i className="las la-check"></i> Cloudflare CDN integration</li>
                                    <li><i className="las la-check"></i> Git Integration</li>
                                    <li><i className="las la-check"></i> Environment Variables</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/s1-web-starter" 
                                   className="nextjs-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>

                            {/* Pro Plan */}
                            <div className="nextjs-pricing-card featured">
                                <div className="nextjs-pricing-badge">Best for Developers</div>
                                <div className="nextjs-pricing-header">
                                    <h3>Next.js Pro</h3>
                                    <p className="nextjs-pricing-tagline">Advanced features for professional Next.js developers</p>
                                </div>
                                <div className="nextjs-pricing-price">
                                    <span className="nextjs-price-amount">$8.99</span>
                                    <span className="nextjs-price-period">USD / Monthly</span>
                                </div>
                                <ul className="nextjs-pricing-features">
                                    <li><i className="las la-check"></i> <strong>Up to 3 Next.js Sites</strong></li>
                                    <li><i className="las la-check"></i> 15GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (Fair Use)</li>
                                    <li><i className="las la-check"></i> Advanced SSR & ISR</li>
                                    <li><i className="las la-check"></i> Edge Functions</li>
                                    <li><i className="las la-check"></i> Preview Deployments</li>
                                    <li><i className="las la-check"></i> Custom Domains</li>
                                    <li><i className="las la-check"></i> Advanced Caching</li>
                                    <li><i className="las la-check"></i> Image Optimization</li>
                                    <li><i className="las la-check"></i> Analytics & Monitoring</li>
                                    <li><i className="las la-check"></i> Priority Build Queue</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/epicfinal-cloud-web-pro" 
                                   className="nextjs-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>

                            {/* Business Plan */}
                            <div className="nextjs-pricing-card">
                                <div className="nextjs-pricing-header">
                                    <h3>Next.js Business</h3>
                                    <p className="nextjs-pricing-tagline">Enterprise-grade hosting for agencies and large applications</p>
                                </div>
                                <div className="nextjs-pricing-price">
                                    <span className="nextjs-price-amount">$15.99</span>
                                    <span className="nextjs-price-period">USD / Monthly</span>
                                </div>
                                <ul className="nextjs-pricing-features">
                                    <li><i className="las la-check"></i> <strong>Up to 10 Next.js Sites</strong></li>
                                    <li><i className="las la-check"></i> 40GB NVMe SSD Storage</li>
                                    <li><i className="las la-check"></i> Unmetered Traffic (~150k Visits/Month)</li>
                                    <li><i className="las la-check"></i> Multi-Tenant Support</li>
                                    <li><i className="las la-check"></i> Advanced Edge Functions</li>
                                    <li><i className="las la-check"></i> Team Collaboration</li>
                                    <li><i className="las la-check"></i> Dedicated Build Resources</li>
                                    <li><i className="las la-check"></i> Priority Support</li>
                                    <li><i className="las la-check"></i> Advanced Security</li>
                                    <li><i className="las la-check"></i> Custom Infrastructure</li>
                                    <li><i className="las la-check"></i> Perfect for Agency Projects</li>
                                </ul>
                                <a href="https://my.epicfinal.com/index.php?rp=/store/cloud-web-hosting/epicfinal-business" 
                                   className="nextjs-pricing-btn" target="_blank" rel="noopener noreferrer">
                                    Get Started
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Stats Section */}
                <section className="nextjs-stats">
                    <div className="container">
                        <div className="nextjs-stats-grid">
                            <div className="nextjs-stat">
                                <div className="nextjs-stat-number">&lt;100ms</div>
                                <div className="nextjs-stat-label">Response Time</div>
                            </div>
                            <div className="nextjs-stat">
                                <div className="nextjs-stat-number">200+</div>
                                <div className="nextjs-stat-label">Edge Locations</div>
                            </div>
                            <div className="nextjs-stat">
                                <div className="nextjs-stat-number">99.99%</div>
                                <div className="nextjs-stat-label">Uptime SLA</div>
                            </div>
                            <div className="nextjs-stat">
                                <div className="nextjs-stat-number">∞</div>
                                <div className="nextjs-stat-label">Auto Scaling</div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="nextjs-cta">
                    <div className="container">
                        <div className="nextjs-cta-content">
                            <h2>Deploy Your Next.js App Today</h2>
                            <p>Experience the fastest hosting platform for modern web applications</p>
                            <div className="nextjs-cta-buttons">
                                <Link to="/pricing" className="btn-nextjs btn-light">
                                    View Pricing
                                </Link>
                                <Link to="/contact1" className="btn-nextjs btn-outline">
                                    Get in Touch
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default NextJsHosting;

